import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Form,
  FormActions,
  FormSection,
  GridFrame,
  GridScroll,
  LoadingState,
  SelectField,
  StatusChip,
  Table,
  Td,
  TextField,
  Th,
  Tr,
  statusTone,
} from '@tessma/design-system';
import { Refusal, refusalText, formatDay, formatMoney, useAsync } from '../ui/async.js';
import type { CatalogueItem, PartiesGateway } from '../parties/gateway.js';
import type {
  Account,
  EntitySettings,
  FinanceDimension,
  FinanceGateway,
  FinancePaymentTerm,
  FinanceReportCatalogueEntry,
  ItemDefaults,
  OpeningPosition,
  RecurringDefinition,
  RecurringOccurrence,
  Revaluation,
  RevaluationLine,
} from './gateway.js';

/**
 * FINANCE SETUP — the four things a tenant decides before, and outside, the
 * day's transactions.
 *
 * **THE UI DECIDES NOTHING, AS EVERYWHERE ELSE IN FINANCE.** It does not work
 * out whether an opening trial balance balances, whether an entity may go
 * live, what a revaluation is worth, or which account an item codes to. Every
 * one of those is computed by the server over the authorised relation, and
 * this screen shows the answer and the refusal.
 *
 * **GOING LIVE IS OFFERED ONLY WHEN THE SERVER SAYS IT BALANCES** — and that
 * is a courtesy, not the control. The database refuses an ordinary posting
 * into an entity still in setup (0063) and the service refuses a go-live whose
 * opening balance does not net to zero, whatever this screen renders.
 */

function moneyOf(value: string, currency: string): string {
  return formatMoney(value, currency);
}

// ---- 1. Opening balances and go-live (FR-SET-005) ---------------------------

export function EntitySetupScreen({
  gateway,
  canManage,
}: {
  readonly gateway: FinanceGateway;
  readonly canManage: boolean;
}) {
  const { state, reload } = useAsync<readonly EntitySettings[]>(
    () => gateway.entitySettings(),
    [gateway],
  );

  if (state.status === 'loading') return <LoadingState title="Loading entity settings…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  if (state.value.length === 0) {
    return (
      <Card>
        <CardHeader title="Entity setup" />
        <EmptyState
          icon="gear-six"
          title="No legal entity within your reach has Finance settings"
          description="Settings are created when an entity is configured for Finance."
        />
      </Card>
    );
  }

  return (
    <div>
      {state.value.map((entity) => (
        <EntityCard
          key={entity.legalEntityId}
          gateway={gateway}
          entity={entity}
          canManage={canManage}
          onChanged={reload}
        />
      ))}
    </div>
  );
}

function EntityCard({
  gateway,
  entity,
  canManage,
  onChanged,
}: {
  readonly gateway: FinanceGateway;
  readonly entity: EntitySettings;
  readonly canManage: boolean;
  readonly onChanged: () => void;
}) {
  const { state, reload } = useAsync<OpeningPosition>(
    () => gateway.openingPosition(entity.legalEntityId),
    [gateway, entity.legalEntityId, entity.lifecycle],
  );
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  const live = entity.lifecycle === 'live';

  async function goLive() {
    setBusy(true);
    setProblem(undefined);
    try {
      await gateway.goLive(entity.legalEntityId);
      onChanged();
      reload();
    } catch (error) {
      // THE SERVER'S SENTENCE. "The opening trial balance does not balance by
      // 250 minor units" is a better message than anything invented here.
      setProblem(refusalText(error, 'The books could not be opened.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader
        title={`Entity ${entity.legalEntityId.slice(0, 8)}`}
        subtitle={`${entity.baseCurrency} · ${entity.accountingBasis} · VAT ${entity.vatScheme}`}
        action={<StatusChip tone={statusTone(entity.lifecycle)}>{entity.lifecycle}</StatusChip>}
      />
      {problem !== undefined ? <Alert tone="danger" title={problem} /> : null}

      {live ? (
        <Alert tone="success" title="The books are open">
          Went live {entity.wentLiveAt === null ? 'at some point' : formatDay(entity.wentLiveAt)}.
          Going live happens once and is not reversible: an entity that has posted a transaction
          cannot be put back into setup without pretending the transaction did not happen.
        </Alert>
      ) : (
        <Alert tone="info" title="Still in setup">
          Only opening balances may be posted. An ordinary transaction is refused by the database,
          not by this screen — so it is refused however it arrives.
        </Alert>
      )}

      {state.status === 'failed' ? <Refusal error={state.error} onRetry={reload} /> : null}
      {state.status === 'ready' ? (
        <GridFrame>
          <GridScroll>
            <Table label="Opening position">
              <thead>
                <Tr>
                  <Th align="right">Opening debits</Th>
                  <Th align="right">Opening credits</Th>
                  <Th>Balances</Th>
                </Tr>
              </thead>
              <tbody>
                <Tr>
                  <Td variant="money">{moneyOf(state.value.debit, entity.baseCurrency)}</Td>
                  <Td variant="money">{moneyOf(state.value.credit, entity.baseCurrency)}</Td>
                  <Td>
                    <StatusChip tone={state.value.balanced ? 'success' : 'warning'}>
                      {state.value.balanced ? 'Yes' : 'No'}
                    </StatusChip>
                  </Td>
                </Tr>
              </tbody>
            </Table>
          </GridScroll>
        </GridFrame>
      ) : null}

      {canManage && !live ? (
        <Form
          onSubmit={(event) => {
            event.preventDefault();
            void goLive();
          }}
        >
          <FormActions
            submitLabel="Open the books"
            submitting={busy}
            note="The server re-checks that the opening balance nets to zero at the moment it happens."
          />
        </Form>
      ) : null}
    </Card>
  );
}

// ---- 2. Item defaults (FR-PRD-002) ------------------------------------------

/**
 * HOW A CATALOGUE ITEM IS CODED IN ONE ENTITY'S BOOKS.
 *
 * The same item bought and sold does not land in the same place, which is why
 * there are two account columns rather than one. A line that names an item and
 * leaves the coding out takes these; a line still uncoded afterwards is
 * REFUSED by the server rather than posted against a guess.
 */
export function ItemDefaultsScreen({
  gateway,
  catalogue,
  legalEntityId,
  canManage,
}: {
  readonly gateway: FinanceGateway;
  /**
   * FR-PRD-001. The shared catalogue, so an item is CHOSEN rather than typed.
   *
   * This field asked for a uuid until the catalogue had a route: the screen
   * could not offer a list of items because nothing could list them, so
   * somebody had to paste an identifier from a database. That is not a
   * usability complaint — a mistyped id codes a document to the wrong item.
   */
  readonly catalogue: Pick<PartiesGateway, 'catalogueItems'>;
  readonly legalEntityId: string;
  readonly canManage: boolean;
}) {
  const { state, reload } = useAsync<{
    items: readonly ItemDefaults[];
    accounts: readonly Account[];
    catalogue: readonly CatalogueItem[];
  }>(
    async () => ({
      items: await gateway.itemDefaults(legalEntityId),
      accounts: await gateway.accounts(legalEntityId),
      catalogue: await catalogue.catalogueItems(),
    }),
    [gateway, catalogue, legalEntityId],
  );

  const [itemId, setItemId] = useState('');
  const [incomeAccountId, setIncomeAccountId] = useState('');
  const [expenseAccountId, setExpenseAccountId] = useState('');
  const [taxCode, setTaxCode] = useState('GB_STD');
  const [unitPrice, setUnitPrice] = useState('');
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  if (state.status === 'loading') return <LoadingState title="Loading item defaults…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  const income = state.value.accounts.filter((account) => account.accountType === 'income');
  const expense = state.value.accounts.filter((account) => account.accountType === 'expense');

  async function save() {
    setSaving(true);
    setProblem(undefined);
    try {
      await gateway.setItemDefaults(legalEntityId, {
        itemId,
        ...(incomeAccountId === '' ? {} : { incomeAccountId }),
        ...(expenseAccountId === '' ? {} : { expenseAccountId }),
        ...(taxCode === '' ? {} : { defaultTaxCode: taxCode }),
        ...(unitPrice === '' ? {} : { defaultUnitPrice: unitPrice }),
      });
      setItemId('');
      setUnitPrice('');
      reload();
    } catch (error) {
      setProblem(refusalText(error, 'The defaults could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <Card padding="flush">
        <CardHeader
          title="Item defaults"
          subtitle="How a catalogue item is coded in this entity’s books. Sold and bought land in different places."
        />
        {state.value.items.length === 0 ? (
          <EmptyState
            icon="list"
            title="No item has defaults here yet"
            description="A line that names an item without a price or an account will be refused until one does."
          />
        ) : (
          <GridFrame>
            <GridScroll>
              <Table label="Item defaults">
                <thead>
                  <Tr>
                    <Th>Item</Th>
                    <Th>Income account</Th>
                    <Th>Expense account</Th>
                    <Th>Tax code</Th>
                    <Th align="right">Price</Th>
                  </Tr>
                </thead>
                <tbody>
                  {state.value.items.map((item) => (
                    <Tr key={item.itemId}>
                      <Td>
                        {item.itemCode} — {item.itemName}
                      </Td>
                      <Td variant="secondary">
                        {accountLabel(state.value.accounts, item.incomeAccountId)}
                      </Td>
                      <Td variant="secondary">
                        {accountLabel(state.value.accounts, item.expenseAccountId)}
                      </Td>
                      <Td variant="secondary">{item.defaultTaxCode ?? '—'}</Td>
                      <Td variant="money">
                        {item.defaultUnitPrice === null
                          ? '—'
                          : moneyOf(item.defaultUnitPrice, 'GBP')}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </GridScroll>
          </GridFrame>
        )}
      </Card>

      {canManage ? (
        <Card>
          <CardHeader title="Set an item’s defaults" />
          {problem !== undefined ? <Alert tone="danger" title={problem} /> : null}
          <Form
            onSubmit={(event) => {
              event.preventDefault();
              void save();
            }}
          >
            <FormSection title="Item">
              <SelectField
                label="Catalogue item"
                value={itemId}
                onChange={(event) => {
                  setItemId(event.target.value);
                }}
                options={state.value.catalogue.map((item) => ({
                  value: item.id,
                  label: `${item.code} — ${item.name}`,
                }))}
                placeholder="Choose an item"
                helper="The shared catalogue's item. These defaults apply in this entity only."
              />
              <SelectField
                label="Income account"
                value={incomeAccountId}
                onChange={(event) => {
                  setIncomeAccountId(event.target.value);
                }}
                options={income.map((account) => ({
                  value: account.id,
                  label: `${account.code} ${account.name}`,
                }))}
                placeholder="Leave unset"
              />
              <SelectField
                label="Expense account"
                value={expenseAccountId}
                onChange={(event) => {
                  setExpenseAccountId(event.target.value);
                }}
                options={expense.map((account) => ({
                  value: account.id,
                  label: `${account.code} ${account.name}`,
                }))}
                placeholder="Leave unset"
              />
              <TextField
                label="Tax code"
                value={taxCode}
                onChange={(event) => {
                  setTaxCode(event.target.value);
                }}
                helper="The shared tax kernel decides the rate from this code and the tax point."
              />
              <TextField
                label="Default unit price, in minor units"
                value={unitPrice}
                onChange={(event) => {
                  setUnitPrice(event.target.value);
                }}
                helper="Pence. A line naming this item may leave its price out; the browser does no arithmetic on it."
              />
            </FormSection>
            <FormActions submitLabel="Save the defaults" submitting={saving} />
          </Form>
        </Card>
      ) : null}
    </div>
  );
}

function accountLabel(accounts: readonly Account[], id: string | null): string {
  if (id === null) return '—';
  const account = accounts.find((row) => row.id === id);
  return account === undefined ? id.slice(0, 8) : `${account.code} ${account.name}`;
}

// ---- 3. Unrealised exchange differences (FR-CUR-003) ------------------------

export function RevaluationScreen({
  gateway,
  legalEntityId,
  canRun,
}: {
  readonly gateway: FinanceGateway;
  readonly legalEntityId: string;
  readonly canRun: boolean;
}) {
  const { state, reload } = useAsync<readonly Revaluation[]>(
    () => gateway.revaluations(legalEntityId),
    [gateway, legalEntityId],
  );
  const [asOf, setAsOf] = useState('');
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<string | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  if (state.status === 'loading') return <LoadingState title="Loading revaluations…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  async function run() {
    setBusy(true);
    setProblem(undefined);
    setOutcome(undefined);
    try {
      const result = await gateway.revalue(legalEntityId, asOf);
      // `created: false` MEANS THIS DATE WAS ALREADY REVALUED and nothing was
      // posted. Said plainly rather than hidden: somebody who cannot tell a
      // fresh run from a replay cannot tell a retry from a duplicate.
      setOutcome(
        result.created
          ? `Revalued. Difference taken to the P&L: ${result.difference} minor units.`
          : 'That date had already been revalued. Nothing was posted.',
      );
      reload();
    } catch (error) {
      setProblem(refusalText(error, 'The revaluation was refused.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <Card>
        <CardHeader
          title="Unrealised exchange differences"
          subtitle="Foreign-currency balances restated at a period end, and reversed the next day."
        />
        {problem !== undefined ? <Alert tone="danger" title={problem} /> : null}
        {outcome !== undefined ? <Alert tone="info" title={outcome} /> : null}
        <Alert tone="info" title="A revaluation is reversed the day after it is posted">
          It did not happen — nothing was paid and nothing was received. Leaving it standing would
          make the next period measure the movement since the last revaluation rather than since
          each transaction.
        </Alert>
        {canRun ? (
          <Form
            onSubmit={(event) => {
              event.preventDefault();
              void run();
            }}
          >
            <FormSection title="Revalue">
              <TextField
                label="As at"
                type="date"
                value={asOf}
                onChange={(event) => {
                  setAsOf(event.target.value);
                }}
                helper="The period end. One revaluation per entity per date; a second is refused."
                required
              />
            </FormSection>
            <FormActions submitLabel="Revalue" submitting={busy} />
          </Form>
        ) : null}
      </Card>

      {state.value.length === 0 ? (
        <Card>
          <EmptyState
            icon="chart-bar"
            title="Nothing has been revalued yet"
            description="An entity holding no foreign currency never needs to."
          />
        </Card>
      ) : (
        state.value.map((run_) => (
          <RevaluationDetail key={run_.id} gateway={gateway} revaluation={run_} />
        ))
      )}
    </div>
  );
}

function RevaluationDetail({
  gateway,
  revaluation,
}: {
  readonly gateway: FinanceGateway;
  readonly revaluation: Revaluation;
}) {
  const { state, reload } = useAsync<readonly RevaluationLine[]>(
    () => gateway.revaluationLines(revaluation.id),
    [gateway, revaluation.id],
  );

  return (
    <Card padding="flush">
      <CardHeader
        title={formatDay(revaluation.asOf)}
        subtitle={
          revaluation.journalId === null
            ? 'Ran, and every rate was unchanged — a real result, recorded as one.'
            : `Posted and reversed · ${revaluation.baseCurrency}`
        }
      />
      {state.status === 'failed' ? <Refusal error={state.error} onRetry={reload} /> : null}
      {state.status === 'ready' && state.value.length > 0 ? (
        <GridFrame>
          <GridScroll>
            <Table label="What was restated">
              <thead>
                <Tr>
                  <Th>Account</Th>
                  <Th>Currency</Th>
                  <Th align="right">Held</Th>
                  <Th align="right">Carried at</Th>
                  <Th>Rate</Th>
                  <Th>Source</Th>
                  <Th align="right">Restated</Th>
                  <Th align="right">Difference</Th>
                </Tr>
              </thead>
              <tbody>
                {state.value.map((line) => (
                  <Tr key={line.id}>
                    <Td>{line.accountCode}</Td>
                    <Td variant="secondary">{line.currency}</Td>
                    <Td variant="money">{moneyOf(line.txnBalance, line.currency)}</Td>
                    <Td variant="money">{moneyOf(line.carryingBase, revaluation.baseCurrency)}</Td>
                    {/* The rate as stored, to eighteen places. Not rounded here:
                        it is the evidence for the figure beside it. */}
                    <Td variant="secondary">{line.rate}</Td>
                    <Td variant="secondary">{line.rateSource}</Td>
                    <Td variant="money">{moneyOf(line.restatedBase, revaluation.baseCurrency)}</Td>
                    <Td variant="money">
                      {moneyOf(line.differenceBase, revaluation.baseCurrency)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </GridScroll>
        </GridFrame>
      ) : null}
    </Card>
  );
}

// ---- 4. Recurring journals, accruals and prepayments (FR-LED-005) -----------

export function RecurringScreen({
  gateway,
  legalEntityId,
  canGenerate,
  canManage = false,
}: {
  readonly gateway: FinanceGateway;
  readonly legalEntityId: string;
  readonly canGenerate: boolean;
  /** `finance.recurring.manage` — pausing is configuration, not generation. */
  readonly canManage?: boolean;
}) {
  const { state, reload } = useAsync<readonly RecurringDefinition[]>(
    () => gateway.recurring(legalEntityId),
    [gateway, legalEntityId],
  );

  if (state.status === 'loading') return <LoadingState title="Loading schedules…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  if (state.value.length === 0) {
    return (
      <Card>
        <CardHeader title="Accruals, prepayments and recurring journals" />
        <EmptyState
          icon="calendar-blank"
          title="No schedules here"
          description="An accrual and a prepayment are the same thing with different accounts; a standing charge is one that does not reverse."
        />
      </Card>
    );
  }

  return (
    <div>
      {state.value.map((definition) => (
        <RecurringCard
          key={definition.id}
          gateway={gateway}
          definition={definition}
          canGenerate={canGenerate}
          canManage={canManage}
          onChanged={reload}
        />
      ))}
    </div>
  );
}

function RecurringCard({
  gateway,
  definition,
  canGenerate,
  canManage,
  onChanged,
}: {
  readonly gateway: FinanceGateway;
  readonly definition: RecurringDefinition;
  readonly canGenerate: boolean;
  /** Pausing is configuration, not generation: a different grant. */
  readonly canManage: boolean;
  readonly onChanged: () => void;
}) {
  const { state, reload } = useAsync<readonly RecurringOccurrence[]>(
    () => gateway.recurringOccurrences(definition.id),
    [gateway, definition.id],
  );
  const [occurrenceDate, setOccurrenceDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<string | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  /**
   * PAUSING IS THE ONLY WAY TO STOP A SCHEDULE, and nothing deletes one: the
   * occurrences it has already posted must still resolve to something that
   * explains them.
   */
  async function setActive(isActive: boolean) {
    setBusy(true);
    setProblem(undefined);
    setOutcome(undefined);
    try {
      await gateway.setRecurringActive(definition.id, isActive);
      onChanged();
    } catch (error) {
      setProblem(refusalText(error, 'The schedule could not be changed.'));
    } finally {
      setBusy(false);
    }
  }

  async function generate() {
    setBusy(true);
    setProblem(undefined);
    setOutcome(undefined);
    try {
      const result = await gateway.generateOccurrence(definition.id, occurrenceDate);
      setOutcome(
        result.created
          ? 'Generated, with its reversal where the definition has one.'
          : 'That date had already been generated. Nothing was posted.',
      );
      reload();
      onChanged();
    } catch (error) {
      // Most likely a closed period, and the server says so.
      setProblem(refusalText(error, 'The occurrence was refused.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader
        title={definition.name}
        subtitle={`${definition.kind} · ${definition.frequency} on day ${String(definition.dayOfMonth)} · ${definition.currency}`}
        action={
          <>
            <StatusChip tone={definition.isActive ? 'success' : 'neutral'}>
              {definition.isActive ? 'active' : 'paused'}
            </StatusChip>
            {canManage ? (
              <Button
                variant="tertiary"
                onClick={() => {
                  void setActive(!definition.isActive);
                }}
              >
                {definition.isActive ? 'Pause' : 'Resume'}
              </Button>
            ) : null}
          </>
        }
      />
      {problem !== undefined ? <Alert tone="danger" title={problem} /> : null}
      {outcome !== undefined ? <Alert tone="info" title={outcome} /> : null}
      {definition.autoReverse ? (
        <Alert tone="info" title="Reversed the day after it posts">
          Which is what makes an accrual an accrual: the estimate belongs to the period that ended
          and is unwound as the next one starts, so the real invoice is not counted twice.
        </Alert>
      ) : null}

      {canGenerate ? (
        <Form
          onSubmit={(event) => {
            event.preventDefault();
            void generate();
          }}
        >
          <FormSection title="Generate an occurrence">
            <TextField
              label="For the date"
              type="date"
              value={occurrenceDate}
              onChange={(event) => {
                setOccurrenceDate(event.target.value);
              }}
              helper="The date the occurrence is FOR, never today. A closed period refuses it."
              required
            />
          </FormSection>
          <FormActions submitLabel="Generate" submitting={busy} />
        </Form>
      ) : null}

      {state.status === 'ready' && state.value.length > 0 ? (
        <GridFrame>
          <GridScroll>
            <Table label="Generated">
              <thead>
                <Tr>
                  <Th>For</Th>
                  <Th>Journal</Th>
                  <Th>Reversal</Th>
                </Tr>
              </thead>
              <tbody>
                {state.value.map((occurrence) => (
                  <Tr key={occurrence.id}>
                    <Td>{formatDay(occurrence.occurrenceDate)}</Td>
                    <Td variant="secondary">{occurrence.journalId?.slice(0, 8) ?? '—'}</Td>
                    <Td variant="secondary">
                      {occurrence.reversalJournalId?.slice(0, 8) ?? 'does not reverse'}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </GridScroll>
        </GridFrame>
      ) : null}
    </Card>
  );
}

// ---- 5. Finance's standard reports (FR-RPT-001, FR-RPT-003) -----------------

export function FinanceReportsScreen({
  gateway,
  canInstall,
}: {
  readonly gateway: FinanceGateway;
  readonly canInstall: boolean;
}) {
  const { state, reload } = useAsync<readonly FinanceReportCatalogueEntry[]>(
    () => gateway.financeReportCatalogue(),
    [gateway],
  );
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<string | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  if (state.status === 'loading') return <LoadingState title="Loading the report catalogue…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  async function install() {
    setBusy(true);
    setProblem(undefined);
    try {
      const installed = await gateway.installFinanceReports();
      const created = installed.filter((row) => row.outcome === 'created').length;
      const updated = installed.filter((row) => row.outcome === 'updated').length;
      setOutcome(
        `${String(created)} installed, ${String(updated)} updated, ` +
          `${String(installed.length - created - updated)} already current.`,
      );
      reload();
    } catch (error) {
      setProblem(refusalText(error, 'The reports could not be installed.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card padding="flush">
      <CardHeader
        title="Finance’s standard reports"
        subtitle="Definitions over the shared reporting framework, so two people with different reach get different and individually correct numbers."
        action={
          canInstall ? (
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => {
                void install();
              }}
            >
              {busy ? 'Installing…' : 'Install or refresh'}
            </Button>
          ) : undefined
        }
      />
      {problem !== undefined ? <Alert tone="danger" title={problem} /> : null}
      {outcome !== undefined ? <Alert tone="success" title={outcome} /> : null}
      <GridFrame>
        <GridScroll>
          <Table label="Standard reports">
            <thead>
              <Tr>
                <Th>Report</Th>
                <Th>Reads</Th>
                <Th>What it is</Th>
              </Tr>
            </thead>
            <tbody>
              {state.value.map((report) => (
                <Tr key={report.key}>
                  <Td>{report.name}</Td>
                  <Td variant="secondary">{report.datasetKey}</Td>
                  <Td variant="secondary">{report.description}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </GridScroll>
      </GridFrame>
    </Card>
  );
}

// ---- 6. Dimensions and payment terms (FR-LED-006, FR-SET-006) ---------------

/**
 * THE TWO THINGS A POSTING REFERS TO THAT NOTHING COULD CREATE.
 *
 * `finance_dimension` and `finance_payment_term` both arrived with 0055; the
 * posting engine has always accepted a cost centre and the customer row has
 * always had a `payment_term_id`, and until now there was no way to write
 * either. A screen matters here more than usual: a dimension nobody can create
 * is a report grouped by nothing, and a payment term nobody can create is a
 * due date retyped on every invoice.
 *
 * THE KIND IS SHOWN, not implied by the list a row was fetched into. One table
 * holds all four kinds, so a department shown under "Projects" would be wrong
 * the moment anybody filtered the other way — and the posting engine refuses a
 * line whose dimension is of the wrong kind for exactly the same reason.
 *
 * NOTHING HERE WORKS OUT A DUE DATE. The term is stored; `dueDateFor` on the
 * server turns it into a date at the moment an invoice is issued. "End of the
 * month following invoice" is a different day in February and in March, and a
 * browser that computed it would be a second opinion about when money is owed.
 */
export function CodingSetupScreen({
  gateway,
  legalEntityId,
  canManage,
}: {
  readonly gateway: FinanceGateway;
  readonly legalEntityId: string;
  readonly canManage: boolean;
}) {
  const { state, reload } = useAsync<{
    dimensions: readonly FinanceDimension[];
    terms: readonly FinancePaymentTerm[];
  }>(
    async () => ({
      dimensions: await gateway.dimensions(legalEntityId),
      terms: await gateway.paymentTerms(legalEntityId),
    }),
    [gateway, legalEntityId],
  );

  const [kind, setKind] = useState<'cost_centre' | 'department' | 'project' | 'job'>('cost_centre');
  const [dimensionCode, setDimensionCode] = useState('');
  const [dimensionName, setDimensionName] = useState('');
  const [savingDimension, setSavingDimension] = useState(false);
  const [dimensionProblem, setDimensionProblem] = useState<string | undefined>(undefined);

  const [termCode, setTermCode] = useState('');
  const [termName, setTermName] = useState('');
  const [basis, setBasis] = useState<'days_after_invoice' | 'end_of_month_plus_days'>(
    'days_after_invoice',
  );
  const [days, setDays] = useState('30');
  const [isDefault, setIsDefault] = useState('no');
  const [savingTerm, setSavingTerm] = useState(false);
  const [termProblem, setTermProblem] = useState<string | undefined>(undefined);

  if (state.status === 'loading') return <LoadingState title="Loading coding setup…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  async function saveDimension() {
    setSavingDimension(true);
    setDimensionProblem(undefined);
    try {
      await gateway.saveDimension({
        legalEntityId,
        dimensionKind: kind,
        code: dimensionCode,
        name: dimensionName,
      });
      setDimensionCode('');
      setDimensionName('');
      reload();
    } catch (error) {
      setDimensionProblem(refusalText(error, 'The dimension could not be saved.'));
    } finally {
      setSavingDimension(false);
    }
  }

  async function savePaymentTerm() {
    setSavingTerm(true);
    setTermProblem(undefined);
    try {
      await gateway.savePaymentTerm({
        legalEntityId,
        code: termCode,
        name: termName,
        basis,
        // The server validates the range. This is a parse of what was typed,
        // not a decision about what is allowed.
        days: Number.parseInt(days, 10),
        isDefault: isDefault === 'yes',
      });
      setTermCode('');
      setTermName('');
      reload();
    } catch (error) {
      setTermProblem(refusalText(error, 'The payment term could not be saved.'));
    } finally {
      setSavingTerm(false);
    }
  }

  return (
    <div>
      <Card padding="flush">
        <CardHeader
          title="Posting dimensions"
          subtitle="Cost centres, departments, projects and jobs. A posting that names one of these is refused unless it exists here, in this entity, of that kind."
        />
        {state.value.dimensions.length === 0 ? (
          <EmptyState
            icon="list"
            title="No dimension in this entity yet"
            description="Until one exists, a posting may carry no cost centre, department, project or job at all."
          />
        ) : (
          <GridFrame>
            <GridScroll>
              <Table label="Posting dimensions">
                <thead>
                  <Tr>
                    <Th>Kind</Th>
                    <Th>Code</Th>
                    <Th>Name</Th>
                    <Th>Active</Th>
                  </Tr>
                </thead>
                <tbody>
                  {state.value.dimensions.map((dimension) => (
                    <Tr key={dimension.id}>
                      <Td variant="secondary">{dimension.dimensionKind.replace('_', ' ')}</Td>
                      <Td>{dimension.code}</Td>
                      <Td>{dimension.name}</Td>
                      <Td>
                        <StatusChip tone={dimension.isActive ? 'success' : 'neutral'}>
                          {dimension.isActive ? 'Active' : 'Inactive'}
                        </StatusChip>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </GridScroll>
          </GridFrame>
        )}
      </Card>

      {canManage ? (
        <Card>
          <CardHeader title="Add or rename a dimension" />
          {dimensionProblem !== undefined ? <Alert tone="danger" title={dimensionProblem} /> : null}
          <Form
            onSubmit={(event) => {
              event.preventDefault();
              void saveDimension();
            }}
          >
            <FormSection title="Dimension">
              <SelectField
                label="Kind"
                value={kind}
                onChange={(event) => {
                  setKind(event.target.value as typeof kind);
                }}
                options={[
                  { value: 'cost_centre', label: 'Cost centre' },
                  { value: 'department', label: 'Department' },
                  { value: 'project', label: 'Project' },
                  { value: 'job', label: 'Job' },
                ]}
              />
              <TextField
                label="Dimension code"
                value={dimensionCode}
                onChange={(event) => {
                  setDimensionCode(event.target.value);
                }}
                helper="Unique within this entity and kind. Saving the same code again renames it."
                required
              />
              <TextField
                label="Dimension name"
                value={dimensionName}
                onChange={(event) => {
                  setDimensionName(event.target.value);
                }}
                required
              />
            </FormSection>
            <FormActions submitLabel="Save the dimension" submitting={savingDimension} />
          </Form>
        </Card>
      ) : null}

      <Card padding="flush">
        <CardHeader
          title="Payment terms"
          subtitle="What a customer’s due date is derived from. The server turns a term into a date when an invoice is issued."
        />
        {state.value.terms.length === 0 ? (
          <EmptyState
            icon="calendar-blank"
            title="No payment term in this entity yet"
            description="Invoices fall back to thirty days from issue until a term is set on the customer."
          />
        ) : (
          <GridFrame>
            <GridScroll>
              <Table label="Payment terms">
                <thead>
                  <Tr>
                    <Th>Code</Th>
                    <Th>Name</Th>
                    <Th>Basis</Th>
                    <Th align="right">Days</Th>
                    <Th>Default</Th>
                  </Tr>
                </thead>
                <tbody>
                  {state.value.terms.map((term) => (
                    <Tr key={term.id}>
                      <Td>{term.code}</Td>
                      <Td>{term.name}</Td>
                      <Td variant="secondary">{basisLabel(term.basis)}</Td>
                      <Td variant="money">{term.days}</Td>
                      <Td>
                        <StatusChip tone={term.isDefault ? 'info' : 'neutral'}>
                          {term.isDefault ? 'Default' : '—'}
                        </StatusChip>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </GridScroll>
          </GridFrame>
        )}
      </Card>

      {canManage ? (
        <Card>
          <CardHeader title="Add or change a payment term" />
          {termProblem !== undefined ? <Alert tone="danger" title={termProblem} /> : null}
          <Form
            onSubmit={(event) => {
              event.preventDefault();
              void savePaymentTerm();
            }}
          >
            <FormSection title="Term">
              <TextField
                label="Term code"
                value={termCode}
                onChange={(event) => {
                  setTermCode(event.target.value);
                }}
                helper="Unique within this entity. Saving the same code again changes the term."
                required
              />
              <TextField
                label="Term name"
                value={termName}
                onChange={(event) => {
                  setTermName(event.target.value);
                }}
                required
              />
              <SelectField
                label="Basis"
                value={basis}
                onChange={(event) => {
                  setBasis(event.target.value as typeof basis);
                }}
                options={[
                  { value: 'days_after_invoice', label: 'Days after the invoice date' },
                  {
                    value: 'end_of_month_plus_days',
                    label: 'End of the month following the invoice, plus days',
                  },
                ]}
                helper="The second is not a day count: it lands on a different date in February and in March."
              />
              <TextField
                label="Days"
                value={days}
                onChange={(event) => {
                  setDays(event.target.value);
                }}
                helper="Zero on the second basis means the end of the following month itself."
                required
              />
              <SelectField
                label="Default for this entity"
                value={isDefault}
                onChange={(event) => {
                  setIsDefault(event.target.value);
                }}
                options={[
                  { value: 'no', label: 'No' },
                  { value: 'yes', label: 'Yes' },
                ]}
              />
            </FormSection>
            <FormActions submitLabel="Save the term" submitting={savingTerm} />
          </Form>
        </Card>
      ) : null}
    </div>
  );
}

function basisLabel(basis: string): string {
  return basis === 'days_after_invoice'
    ? 'Days after invoice'
    : 'End of the following month, plus days';
}
